import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const NUM_FIELDS = [
  ["interval_months", "Service interval (months)"],
  ["interval_km", "Service interval (km)"],
  ["due_soon_days", "Due-soon window (days)"],
  ["due_soon_km", "Due-soon window (km)"],
];

export const ServiceSettingsDialog = ({ open, onClose, onSaved }) => {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null);
    api.get("/fleet/service-settings").then((r) => {
      setCfg(r.data);
      setRecipients((r.data.digest_recipients || []).join(", "));
    });
  }, [open]);

  if (!open) return null;

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = Object.fromEntries(NUM_FIELDS.map(([k]) => [k, Number(cfg[k]) || null]));
      body.digest_enabled = !!cfg.digest_enabled;
      body.digest_recipients = recipients.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/fleet/service-settings", body);
      setCfg(data); setRecipients((data.digest_recipients || []).join(", "));
      setMsg({ ok: true, text: "Saved." });
      onSaved?.();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Save failed" }); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/fleet/service-digest/send");
      setMsg(data.skipped
        ? { ok: false, text: `Not sent — ${data.skipped}` }
        : { ok: data.sent > 0, text: `Digest sent to ${data.sent} recipient(s) — ${data.overdue} overdue, ${data.due_soon} due soon.` });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Send failed" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg p-5 w-[460px] max-w-[92vw]" onClick={(e) => e.stopPropagation()} data-testid="fleet-settings-dialog">
        <h3 className="font-display font-semibold text-slate-900">Service alerts &amp; intervals</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">A vehicle is due when either the time or km interval passes — whichever comes first.</p>
        {!cfg ? <div className="h-32 shimmer rounded" /> : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {NUM_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
                  <Input data-testid={`fleet-setting-${k}`} type="number" value={cfg[k] ?? ""} className="h-8 text-sm"
                    onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Weekly digest recipients (comma-separated emails)</div>
              <Input data-testid="fleet-setting-recipients" value={recipients} placeholder="e.g. jon.sab@gbegroup.com.au"
                onChange={(e) => setRecipients(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Switch data-testid="fleet-setting-digest-enabled" checked={!!cfg.digest_enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, digest_enabled: v })} />
              <span className="text-sm text-slate-700">Weekly email digest (Mondays 7am)</span>
            </div>
            {msg && (
              <div data-testid="fleet-settings-msg" className={`mt-3 text-xs rounded-md px-3 py-2 ${msg.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{msg.text}</div>
            )}
            <div className="mt-4 flex items-center justify-between">
              <button data-testid="fleet-digest-send-now" disabled={busy} onClick={sendNow}
                className="px-3 py-1.5 text-xs border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50">
                Send digest now
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-200 rounded-md">Close</button>
                <button data-testid="fleet-settings-save" disabled={busy} onClick={save}
                  className="px-3 py-1.5 text-xs bg-[#00334C] text-white rounded-md">{busy ? "Working…" : "Save"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
